"use client";

import React from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel } from "swiper/modules";
import { ArrowUpRightIcon } from "@heroicons/react/16/solid";
import "swiper/css";

import BottomCurves from "../curves/bottomCurves";
import FloatingArrow from "../uiDecor/floatingArrow";
import { getStrapiMedia } from "@/utils/api-helpers";

type PortfolioItem = {
    id?: number | string;
    title?: string;
    redirectLink?: string;
    image?: {
        url?: string;
    };
    serviceType: string;
};

function WorkCard({ portfolioItem }: { portfolioItem: PortfolioItem }) {
    const imageSrc = portfolioItem.image?.url
        ? getStrapiMedia(portfolioItem.image.url)
        : "";
    const link = portfolioItem.redirectLink || "#";
    const heading = portfolioItem.title || "";
    const serviceType = portfolioItem.serviceType || "";

    return (
        <a
            href={link}
            className="relative group flex flex-col sm:flex-row sm:items-stretch rounded-[1.75rem] border border-white/70 bg-white/30 p-4 sm:p-5 lg:p-6 transition-colors hover:bg-white/45 h-full"
        >
            <div className="relative w-full overflow-hidden rounded-2xl sm:max-w-[42%] sm:min-w-[42%] lg:max-w-[44%] lg:min-w-[44%]">
                {imageSrc ? (
                    <Image
                        src={imageSrc}
                        alt={heading}
                        width={560}
                        height={560}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                    />
                ) : (
                    <div className="h-[240px] w-full bg-white/40 sm:h-full" />
                )}
            </div>

            <div className="flex flex-1 flex-col pt-4 text-left sm:pl-6 sm:pt-1">
                <p className="mb-3 text-[1rem] font-medium text-[#5A6474]">
                    {serviceType}
                </p>
                <h3 className="mb-6 text-[1.4rem] md:text-[1.8rem] font-semibold leading-[1.05] text-[#1f2c43] lg:max-w-[20ch]">
                    {heading}
                </h3>
                <span className=" inline-flex h-14 w-14 items-center justify-center self-end rounded-full text-white transition-transform duration-300 group-hover:scale-105"></span>
            </div>
            <span className="absolute bottom-5 right-5 inline-flex h-14 w-14 items-center justify-center self-end rounded-full bg-purple text-white transition-transform duration-300 group-hover:scale-105">
                <ArrowUpRightIcon className="h-8 w-8" />
            </span>
        </a>
    );
}

export default function WorkList({ data }: any) {
    const portfolioCards: PortfolioItem[] = data?.portfolioCard ?? [];

    return (
        <section
            className={` bg-cover ${
                data.isBgRainbow ? "bg-rainbow" : "bg-lightGrey"
            } bg-no-repeat pb-[8vw] md:pb-[2vw] pt-28 lg:pt-[4vw] px-8 xl:px-[10.625vw]`}
        >
            <FloatingArrow
                direction={"right"}
                className="top-0 right-[10.625vw]"
                wrapText="discover"
            />

            {data.heading && (
                <h2 className="text-center text-purple">{data.heading}</h2>
            )}

            {data.description && (
                <p className="w-full text-left">{data.description}</p>
            )}

            <div className="mt-10">
                <div className="flex flex-col gap-4 md:hidden">
                    {portfolioCards.map(
                        (portfolioItem: PortfolioItem, index) => (
                            <WorkCard
                                key={portfolioItem.id ?? `work-mobile-${index}`}
                                portfolioItem={portfolioItem}
                            />
                        ),
                    )}
                </div>

                <div className="hidden md:block sm:-mr-8 xl:-mr-[10.625vw]">
                    <div className="sm:pr-8 xl:pr-[10.625vw]">
                        <Swiper
                            modules={[Mousewheel]}
                            mousewheel={{ forceToAxis: true }}
                            slidesPerView={2.2}
                            centeredSlides={false}
                            watchSlidesProgress={true}
                            grabCursor={true}
                            breakpoints={{
                                640: {
                                    slidesPerView: 2.2,
                                    spaceBetween: 16,
                                },
                                1024: {
                                    slidesPerView: 2.2,
                                    spaceBetween: 24,
                                },
                            }}
                            className="work-list-swiper !overflow-visible pb-4 relative z-0"
                        >
                            {portfolioCards.map(
                                (
                                    portfolioItem: PortfolioItem,
                                    index: number,
                                ) => (
                                    <SwiperSlide
                                        key={
                                            portfolioItem.id ?? `slide-${index}`
                                        }
                                        className="!mb-2 !h-auto !bg-transparent !items-stretch !justify-stretch"
                                    >
                                        <WorkCard
                                            portfolioItem={portfolioItem}
                                        />
                                    </SwiperSlide>
                                ),
                            )}
                        </Swiper>
                    </div>
                </div>
            </div>

            {data.bottomSpacing && (
                <div className="h-20 md:h-28 lg:h-36 xl:h-48 2xl:h-56"></div>
            )}

            {data.waveBgColor !== null && (
                <BottomCurves
                    // color="rainbow purple to blue"
                    // color={`${
                    //     data.hasGradient ? "light grey" : "rainbow purple to blue"
                    // }`}
                    color={`${
                        data.waveBgColor
                            ? data.waveBgColor.waveBgColor
                            : "dark purple"
                    }`}
                    direction="right"
                    className="z-10"
                />
            )}
        </section>
    );
}
